/**
 * BACKOFFICE LOGISTIQUE — Façade (#662).
 *
 * Ancien god-service (~2 390 lignes, ~120 méthodes DAO Firestore) découpé
 * par domaine (workstream C, plan architecture-cleanup-fat-components) :
 *   - ReceptionService          — réception, pesée, écarts de poids, préparation
 *   - ContainerService          — conteneurs, workflow expédition, dégroupage
 *   - DeliveryService           — livraison, retour, réexpédition
 *   - PartnerService            — partenaires livraison/expédition, équipe, roster logistique
 *   - BackofficeActivityService — opérateur, géoloc, activity events, notifications
 *   - backoffice-mapper.ts      — mappers purs doc Firestore → modèles
 *
 * Choix FAÇADE DÉLÉGANTE (vs dissolution) : 40+ composants importent
 * BackofficeService — le service conserve son API publique à l'identique et
 * délègue aux services de domaine (zéro changement chez les appelants,
 * risque minimal). Les nouveaux écrans doivent injecter directement le
 * service de domaine concerné.
 *
 * Restent implémentées ici : les lectures transverses dashboard/listes
 * (stats + listes multi-phases + historique) qui n'appartiennent à aucun
 * domaine métier en particulier.
 */

import { Injectable } from '@angular/core';
import { Firestore, collection, query, where, getDocs, getCountFromServer, doc, getDoc, orderBy, limit } from '@angular/fire/firestore';
import {
  BackofficeStats,
  ShipmentListItem,
  ReceptionSearchResult,
  ShippingContainer,
  DeliveryPartner,
  TeamMember,
  ShippingPartner,
  LogisticsTeamMember,
  ShippingMethodSubType,
  LogisticsRole,
  ActivityCategory,
  WeighResult,
  VmPendingPurchaseListItem
} from '../models/backoffice.models';
import { normalizeVmOrderTypes } from '../../vm/models/vm-account.model';
import { ALL_PHASES, classifyShipmentType, compareByCreatedAtDesc, derivePhaseFromStatus, mapToListItem } from './backoffice-mapper';
import { BackofficeActivityService } from './backoffice-activity.service';
import { IncidentType, PackagingChoice, ReceptionService } from './reception.service';
import { ContainerService } from './container.service';
import { GroupageScanCandidate } from '../utils/groupage-scan-refusal';
import { DeliveryService, DeliveryListPage, DELIVERY_LIST_LIMIT } from './delivery.service';
import { PartnerService } from './partner.service';

/**
 * #416 / #987 — plafond de l'échantillon des breakdowns `byPhase`/`byRootPhase`.
 * Au-delà, les compteurs par onglet sont des MINORANTS : `getStats()` le signale
 * via `sampleCapped` et l'écran le dit, au lieu de laisser croire à un total.
 */
const STATS_SAMPLE_LIMIT = 2000;

@Injectable({ providedIn: 'root' })
export class BackofficeService {

  constructor(
    private firestore: Firestore,
    private activity: BackofficeActivityService,
    private receptionService: ReceptionService,
    private containerService: ContainerService,
    private deliveryService: DeliveryService,
    private partnerService: PartnerService
  ) {}

  // ── Dashboard Stats ──

  async getStats(): Promise<BackofficeStats> {
    const shipmentsRef = collection(this.firestore, 'shipments');
    const stats: BackofficeStats = {
      totalShipments: 0,
      byType: { standard: 0, heavy: 0, vm: 0 },
      byPhase: {},
      byRootPhase: {},
      withoutRootPhase: 0,
      sampleCapped: false,
      sampleSize: 0,
      receptionToday: 0,
      pendingWeighing: 0,
      pendingPreparation: 0,
      openContainers: 0,
      pendingDeliveries: 0,
    };

    // #416 — totalShipments exact via aggregation server-side (getCountFromServer),
    // plus le `allSnap.size` cappé à 2000 qui devenait faux à l'échelle.
    const totalCountSnap = await getCountFromServer(shipmentsRef);
    stats.totalShipments = totalCountSnap.data().count;

    // ⚠️ Limitation connue (#416) : les breakdowns byPhase/byType et les
    // compteurs entrepôt restent calculés sur un ÉCHANTILLON des 2000 docs les
    // plus récents. Un comptage serveur exact par phase est impossible tant que
    // la phase vit dans 3 conventions (currentPhase / phase.current / dérivée de
    // status.current) — il faudrait un backfill de normalisation d'abord. Les
    // 2000 plus récents couvrent largement l'activité opérationnelle courante
    // (réception du jour, pesées en attente).
    const statsQuery = query(shipmentsRef, orderBy('timestamps.createdAt', 'desc'), limit(STATS_SAMPLE_LIMIT));
    const allSnap = await getDocs(statsQuery);
    stats.sampleSize = allSnap.size;
    stats.sampleCapped = allSnap.size >= STATS_SAMPLE_LIMIT;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    allSnap.docs.forEach(d => {
      const data = d.data();
      const phase = data['currentPhase']
        || data['phase']?.['current']
        || derivePhaseFromStatus(data['status']?.['current'])
        || 'unknown';

      // Count by phase (whatever the value is)
      stats.byPhase[phase] = (stats.byPhase[phase] || 0) + 1;

      // #987 — comptage ALIGNÉ SUR LA LISTE : `getAllShipmentsForList` filtre
      // `where('currentPhase','in',…)`, donc un colis sans champ RACINE est
      // compté par `byPhase` mais ne sera JAMAIS listé (3 docs sur 62 mesurés
      // sur befret-acceptance). On le sort du comptage des onglets et on
      // l'expose à part, pour qu'il soit affiché au lieu d'être noyé.
      const rootPhase = data['currentPhase'];
      if (typeof rootPhase === 'string' && rootPhase.length > 0) {
        stats.byRootPhase[rootPhase] = (stats.byRootPhase[rootPhase] || 0) + 1;
      } else {
        stats.withoutRootPhase++;
      }

      // Count by type
      // #1343 — classification PARTAGÉE avec le filtre `?type=` de la liste
      // Colis (`classifyShipmentType`). Deux copies de cette règle donnent un
      // compteur qui annonce N et une liste filtrée qui en montre M.
      stats.byType[classifyShipmentType(data['type'])]++;

      // Warehouse-specific counts
      if (phase === 'warehouse') {
        const warehouseArrival = data['befretIntegration']?.['warehouseArrival']?.toDate?.()
          || data['warehouse']?.['receivedAt']?.toDate?.();
        if (warehouseArrival && warehouseArrival >= today) {
          stats.receptionToday++;
        }
        if (!data['warehouse']?.['befretWeight']) {
          stats.pendingWeighing++;
        }
        if (data['warehouse']?.['befretWeight'] && !data['preparation']?.['verifiedAt']) {
          stats.pendingPreparation++;
        }
      }
    });

    // Log unknown phases for debugging
    const knownPhases = new Set(ALL_PHASES);
    const unknownPhases = Object.keys(stats.byPhase).filter(p => !knownPhases.has(p));
    if (unknownPhases.length > 0) {
      console.warn('⚠️ [Stats] Unknown phases in Firestore:', unknownPhases.map(p => `${p}: ${stats.byPhase[p]}`));
      // Log the actual unknown docs (no phase resolved at all)
      const unknownDocs = allSnap.docs.filter(d => {
        const data = d.data();
        return !data['currentPhase'] && !data['phase']?.['current'] && !derivePhaseFromStatus(data['status']?.['current']);
      }).slice(0, 5);
      unknownDocs.forEach(d => {
        const data = d.data();
        console.warn('⚠️ [Stats] Truly unknown doc:', d.id, {
          statusCurrent: data['status']?.['current'],
          phaseCurrent: data['phase']?.['current'],
          type: data['type'],
          trackingNumber: data['trackingNumber'],
          allTopLevelKeys: Object.keys(data).join(', ')
        });
      });
    }

    // Count open containers
    const containersRef = collection(this.firestore, 'containers');
    const openContainersQ = query(containersRef, where('status', '==', 'open'));
    const openContainersSnap = await getDocs(openContainersQ);
    stats.openContainers = openContainersSnap.size;

    // Count pending deliveries
    stats.pendingDeliveries = stats.byPhase['congo_distribution'] || 0;

    return stats;
  }

  // ── Shipment List ──

  async getShipmentsByPhases(phases: string[], limitCount = 200): Promise<ShipmentListItem[]> {
    const shipmentsRef = collection(this.firestore, 'shipments');

    // Firestore 'in' max 10 values, but we should be fine
    const q = query(
      shipmentsRef,
      where('currentPhase', 'in', phases),
      limit(limitCount)
    );
    const snap = await getDocs(q);

    return snap.docs.map(d => mapToListItem(d)).sort(compareByCreatedAtDesc);
  }

  /**
   * #1122 — colis VM d'UNE phase, pour la vue « Travail VM du jour ».
   *
   * Deux égalités seulement (`parcelInfo.type` + `currentPhase`), **pas
   * d'`orderBy`** : l'index composite `(parcelInfo.type, currentPhase)` existe
   * déjà dans `firestore.indexes.json` (posé pour #1045), donc AUCUN index à
   * créer. Le tri « plus ancien d'abord » se fait en mémoire, comme
   * `vm-unidentified-packages` — un `orderBy('timestamps.createdAt')` aurait
   * exigé le 3ᵉ champ d'index, et les colis convergés (phase `warehouse`) ne
   * sont de toute façon pas triés par leur date de création mais par leur date
   * de paiement.
   *
   * Le filtre de provenance porte sur `parcelInfo.type` et non sur
   * `transportProvider` : le webhook de paiement VM écrit les deux
   * (`handle-vm-payment-webhook.ts`), mais `parcelInfo.type` est le champ
   * canonique (T25-W) que #1040 a explicitement remis en place comme
   * « attribut par lequel le backoffice retrouve les colis VM », et le seul
   * des deux à être indexé avec `currentPhase`.
   *
   * Lecture seule : aucune écriture, aucun effet de bord.
   */
  async getVmShipmentsByPhase(phase: string, limitCount = 200): Promise<ShipmentListItem[]> {
    const shipmentsRef = collection(this.firestore, 'shipments');
    const q = query(
      shipmentsRef,
      where('parcelInfo.type', '==', 'virtual_mailbox'),
      where('currentPhase', '==', phase),
      limit(limitCount)
    );
    const snap = await getDocs(q);
    return snap.docs.map(d => mapToListItem(d));
  }

  /**
   * #1153 — les ACHATS DÉCLARÉS non encore reçus, pour la file « Attendus » de
   * la vue « Travail VM du jour ». Ce ne sont pas des colis (cf.
   * `VmPendingPurchaseListItem`) : aucun document `shipments` n'existe encore.
   *
   * UN SEUL filtre (`status in [...]`), **pas d'`orderBy`** : c'est un filtre
   * mono-champ, servi par l'index simple automatique — donc **aucun index
   * composite à créer**. Le tri « les plus anciens d'abord » se fait en
   * mémoire, comme `getVmShipmentsByPhase` et `vm-unidentified-packages`.
   *
   * ⚠️ Ne PAS ajouter `orderBy('registeredAt','asc')` en croyant l'index
   * existant suffisant : `firestore.indexes.json` porte
   * `(status ASC, registeredAt DESC)`, qui ne sert que lui-même et son
   * renversement total — pas `(status ASC, registeredAt ASC)`. Un index
   * manquant avalé en silence a déjà tué le rapprochement VM (#1113).
   *
   * Lecture seule. Les rules autorisent les employés à lire `vmPendingPurchases`
   * (`firestore.rules` : `allow read: … || isEmployee()`) ; l'écriture reste
   * CF-only.
   */
  async getVmPendingPurchases(limitCount = 200): Promise<VmPendingPurchaseListItem[]> {
    const purchasesRef = collection(this.firestore, 'vmPendingPurchases');
    const q = query(
      purchasesRef,
      where('status', 'in', ['awaiting_arrival', 'partially_received']),
      limit(limitCount)
    );
    const snap = await getDocs(q);

    return snap.docs.map(d => {
      const data: any = d.data();
      return {
        id: d.id,
        boxNumber: data['boxNumber'] || '',
        merchant: data['merchant'] || '',
        orderNumber: data['orderNumber'] || '',
        externalTracking: data['externalTracking'] || null,
        description: data['description'] || null,
        estimatedValue: data['estimatedValue'] ?? null,
        // #1351 — la file backoffice conserve tous les types et reste lisible
        // pour les achats historiques dont Firestore porte encore un scalaire.
        orderTypes: normalizeVmOrderTypes(data['orderType']),
        // `registeredAt` est un serverTimestamp : Timestamp Firestore, ou null
        // le temps que le serveur le résolve sur un doc tout juste créé.
        registeredAt: data['registeredAt']?.toDate?.() || new Date(),
        expectedPackageCount: data['expectedPackageCount'] || 1,
        receivedPackageCount: data['receivedPackageCount'] || 0,
        status: data['status'] || '',
      };
    });
  }

  /**
   * Lots parallèles par groupe de phases (pour éviter une query unique énorme).
   *
   * #1153 — les lots suivent désormais le même découpage que les onglets
   * (`SHIPMENT_LIST_TABS`) : pipeline commun d'un côté, couloirs
   * pré-convergence lourd et VM de l'autre. Le résultat est identique (union
   * inchangée = `ALL_PHASES`) mais un lecteur n'a plus à réconcilier deux
   * découpages différents du même domaine — et le lot `heavy` ne mélange plus
   * la livraison finale en RDC (`heavy_delivery`, post-convergence) avec le
   * devis et la collecte chez le client.
   */
  async getAllShipments(limitCount = 500): Promise<ShipmentListItem[]> {
    const [collection_, heavyIntake, vmStock, warehouse, transit, distribution, delivered, incident] = await Promise.all([
      // Pré-entrepôt standard
      this.getShipmentsByPhases(['preparation', 'order', 'dpd_collection', 'dpd_phase', 'collected_europe'], limitCount),
      // Couloir lourd pré-convergence (colis encore chez le client)
      this.getShipmentsByPhases(['heavy_processing', 'heavy_collection'], limitCount),
      // Couloir VM pré-convergence (colis chez nous, non payé)
      // #1121 — `vm_consolidation` (phase morte, jamais écrite) → `vm_unidentified`.
      this.getShipmentsByPhases(['vm_reception', 'vm_unidentified'], limitCount),
      this.getShipmentsByPhases(['warehouse', 'return_to_sender'], limitCount),
      this.getShipmentsByPhases(['befret_transit', 'befret_phase'], limitCount),
      this.getShipmentsByPhases(['congo_distribution', 'heavy_delivery'], limitCount),
      this.getShipmentsByPhases(['delivered'], limitCount),
      // #1401 — parcours interrompu (perdu, saisi, endommagé…). Sans ce lot, la
      // phase `incident` n'est ramenée par aucune query : le colis est compté
      // par `byPhase` et listé nulle part.
      this.getShipmentsByPhases(['incident'], limitCount),
    ]);

    // Concat + dedup by ID
    const seen = new Set<string>();
    return [...collection_, ...heavyIntake, ...vmStock, ...warehouse, ...transit, ...distribution, ...delivered, ...incident]
      .filter(item => {
        if (seen.has(item.id)) return false;
        seen.add(item.id);
        return true;
      })
      .sort(compareByCreatedAtDesc);
  }

  // ── Shipment List ──
  // Loads ALL docs, resolves phase for legacy docs, filters client-side.
  // Fine for <5000 docs. For larger collections, migrate legacy docs to add currentPhase.

  async getAllShipmentsForList(phases: string[] | null): Promise<ShipmentListItem[]> {
    const shipmentsRef = collection(this.firestore, 'shipments');

    let docs;
    if (phases && phases.length > 0) {
      // #1343 — Firestore plafonne un `in` à 10 valeurs. Le repli précédent
      // lisait TOUTE la collection (limit 1000) puis filtrait côté client :
      // au-delà de 1000 docs, l'onglet perdait des colis SANS RIEN DIRE.
      // Aucun onglet ne dépasse 10 phases aujourd'hui (`shipment-list-tabs.spec.ts`
      // le verrouille), mais découper la requête retire le piège au lieu de
      // dépendre de ce verrou.
      const chunks: string[][] = [];
      for (let i = 0; i < phases.length; i += 10) chunks.push(phases.slice(i, i + 10));

      const snaps = await Promise.all(chunks.map(chunk => getDocs(query(shipmentsRef,
        where('currentPhase', 'in', chunk),
        orderBy('timestamps.createdAt', 'desc'),
        limit(500)
      ))));

      const seen = new Set<string>();
      docs = snaps.flatMap(s => s.docs).filter(d => {
        if (seen.has(d.id)) return false;
        seen.add(d.id);
        return true;
      });
    } else {
      // All phases — use limit to avoid full scan
      const snap = await getDocs(query(shipmentsRef,
        orderBy('timestamps.createdAt', 'desc'),
        limit(1000)
      ));
      docs = snap.docs;
    }

    const items = docs.map(d => {
      const item = mapToListItem(d);
      if (!item.currentPhase || item.currentPhase === 'unknown') {
        const data = d.data();
        item.currentPhase = data['phase']?.['current']
          || derivePhaseFromStatus(data['status']?.['current'])
          || 'unknown';
      }
      return item;
    });

    // Sort by date desc (client-side for consistency)
    items.sort((a, b) => {
      const aTime = a.createdAt?.getTime() || 0;
      const bTime = b.createdAt?.getTime() || 0;
      return bTime - aTime;
    });

    return items;
  }

  // ── Historique ──

  async getShipmentFullHistory(shipmentId: string): Promise<any> {
    const docRef = doc(this.firestore, 'shipments', shipmentId);
    const snap = await getDoc(docRef);
    if (!snap.exists()) return null;
    return { id: snap.id, ...snap.data() };
  }

  // ══ Délégations — Réception / pesée / préparation (ReceptionService) ══

  searchByDpdCode(code: string): Promise<ReceptionSearchResult | null> {
    return this.receptionService.searchByDpdCode(code);
  }

  registerReception(shipmentId: string, trackingNumber: string, senderInfo: { name: string; email: string; phone: string }): Promise<void> {
    return this.receptionService.registerReception(shipmentId, trackingNumber, senderInfo);
  }

  saveBefretWeight(shipmentId: string, befretWeight: number): Promise<WeighResult> {
    return this.receptionService.saveBefretWeight(shipmentId, befretWeight);
  }

  calculateWeightSurcharge(shipmentId: string, befretWeight: number): Promise<any> {
    return this.receptionService.calculateWeightSurcharge(shipmentId, befretWeight);
  }

  createWeightSurchargePaymentLink(shipmentId: string, photo?: string | null): Promise<any> {
    return this.receptionService.createWeightSurchargePaymentLink(shipmentId, photo);
  }

  createWeightCreditPromoCode(shipmentId: string): Promise<any> {
    return this.receptionService.createWeightCreditPromoCode(shipmentId);
  }

  resolveWeightDiscrepancy(shipmentId: string, resolution: 'waive' | 'cancel', notes?: string): Promise<any> {
    return this.receptionService.resolveWeightDiscrepancy(shipmentId, resolution, notes);
  }

  uploadPhoto(base64: string, folder: string, filename: string): Promise<string> {
    return this.receptionService.uploadPhoto(base64, folder, filename);
  }

  getShipmentsForWeighing(): Promise<ShipmentListItem[]> {
    return this.receptionService.getShipmentsForWeighing();
  }

  getRecentReceptions(daysBack = 1): Promise<ShipmentListItem[]> {
    return this.receptionService.getRecentReceptions(daysBack);
  }

  getShipmentsForPreparation(limitCount = 200): Promise<ShipmentListItem[]> {
    return this.receptionService.getShipmentsForPreparation(limitCount);
  }

  getShipmentsWithPendingDiscrepancy(): Promise<ShipmentListItem[]> {
    return this.receptionService.getShipmentsWithPendingDiscrepancy();
  }

  getShipmentsForDestinationSorting(limitCount = 200): Promise<ShipmentListItem[]> {
    return this.receptionService.getShipmentsForDestinationSorting(limitCount);
  }

  confirmDestinationSorting(shipmentId: string): Promise<void> {
    return this.receptionService.confirmDestinationSorting(shipmentId);
  }

  getShipmentsForClassification(limitCount = 500): Promise<ShipmentListItem[]> {
    return this.receptionService.getShipmentsForClassification(limitCount);
  }

  getShipmentForPreparation(shipmentId: string): Promise<any> {
    return this.receptionService.getShipmentForPreparation(shipmentId);
  }

  savePreparationVerification(
    shipmentId: string,
    data: { condition: string; conditionPhotos?: string[]; conditionNotes?: string; verifiedBy?: string }
  ): Promise<void> {
    return this.receptionService.savePreparationVerification(shipmentId, data);
  }

  saveBefretLabel(
    shipmentId: string, labelUrl: string, transportMode: string, destinationZone: string,
    packagingChoice?: PackagingChoice | null,
  ): Promise<void> {
    return this.receptionService.saveBefretLabel(shipmentId, labelUrl, transportMode, destinationZone, packagingChoice);
  }

  savePackagingPhoto(shipmentId: string, photoUrl: string): Promise<void> {
    return this.receptionService.savePackagingPhoto(shipmentId, photoUrl);
  }

  flagShipmentIncident(
    shipmentId: string,
    trackingNumber: string,
    incident: IncidentType,
    senderInfo: { name: string; email: string; phone: string },
    conditionPhotos: string[] = [],
    notes?: string
  ): Promise<void> {
    return this.receptionService.flagShipmentIncident(shipmentId, trackingNumber, incident, senderInfo, conditionPhotos, notes);
  }

  /** #1992 — notification d'incident rappelée par « Vérifier » quand la réponse de la transition a été perdue. */
  sendIncidentNotification(
    trackingNumber: string,
    incident: IncidentType,
    senderInfo: { name: string; email: string; phone: string },
    notes: string | undefined,
    shipmentId: string,
  ): Promise<unknown> {
    return this.receptionService.sendIncidentNotification(trackingNumber, incident, senderInfo, notes, shipmentId);
  }

  resolveShipmentIncident(shipmentId: string, resolution: string): Promise<void> {
    return this.receptionService.resolveShipmentIncident(shipmentId, resolution);
  }

  completePreparation(shipmentId: string, trackingNumber: string, senderInfo: { name: string; email: string; phone: string }): Promise<boolean> {
    return this.receptionService.completePreparation(shipmentId, trackingNumber, senderInfo);
  }

  // ══ Délégations — Conteneurs / expédition / dégroupage (ContainerService) ══

  getContainers(status?: string, limitCount = 100): Promise<ShippingContainer[]> {
    return this.containerService.getContainers(status, limitCount);
  }

  /** #1992 — « Vérifier » : relecture au serveur, jamais au cache local. */
  getShipmentFromServer(shipmentId: string): Promise<any> {
    return this.receptionService.getShipmentFromServer(shipmentId);
  }

  /** #1992 — « Vérifier » : relecture au serveur, jamais au cache local. */
  getContainerFromServer(containerId: string): Promise<ShippingContainer | null> {
    return this.containerService.getContainerFromServer(containerId);
  }

  getContainer(containerId: string): Promise<ShippingContainer | null> {
    return this.containerService.getContainer(containerId);
  }

  createContainer(data: {
    transportMode: 'air' | 'maritime';
    maxWeightType?: 'air_23' | 'maritime_32' | 'custom';
    customMaxWeight?: number;
    destinationCity: string;
    contactChain?: any[];
    notes?: string;
    createdBy?: string;
  }): Promise<string> {
    return this.containerService.createContainer(data);
  }

  assignShipmentToContainer(shipmentId: string, containerId: string, containerNumber: string, shipmentWeight: number): Promise<void> {
    return this.containerService.assignShipmentToContainer(shipmentId, containerId, containerNumber, shipmentWeight);
  }

  removeShipmentFromContainer(shipmentId: string, containerId: string, shipmentWeight: number): Promise<void> {
    return this.containerService.removeShipmentFromContainer(shipmentId, containerId, shipmentWeight);
  }

  closeContainer(containerId: string, sealNumber: string, departureWeight: number): Promise<void> {
    return this.containerService.closeContainer(containerId, sealNumber, departureWeight);
  }

  dispatchContainer(containerId: string, estimatedArrival: Date): Promise<void> {
    return this.containerService.dispatchContainer(containerId, estimatedArrival);
  }

  getShipmentsInContainer(containerId: string): Promise<ShipmentListItem[]> {
    return this.containerService.getShipmentsInContainer(containerId);
  }

  getShipmentsReadyForExpedition(limitCount = 200): Promise<ShipmentListItem[]> {
    return this.containerService.getShipmentsReadyForExpedition(limitCount);
  }

  getShipmentsReadyForExpeditionPage(limitCount = 200): Promise<{ items: ShipmentListItem[]; truncated: boolean }> {
    return this.containerService.getShipmentsReadyForExpeditionPage(limitCount);
  }

  findGroupageScanCandidate(code: string): Promise<GroupageScanCandidate | null> {
    return this.receptionService.findGroupageScanCandidate(code);
  }

  updateContainerContactChain(containerId: string, contactChain: any[]): Promise<void> {
    return this.containerService.updateContainerContactChain(containerId, contactChain);
  }

  searchContainerByNumber(containerNumber: string): Promise<ShippingContainer | null> {
    return this.containerService.searchContainerByNumber(containerNumber);
  }

  confirmContainerReception(containerId: string, condition: 'good' | 'damaged' | 'partial', receivedBy: string): Promise<void> {
    return this.containerService.confirmContainerReception(containerId, condition, receivedBy);
  }

  weighContainerArrival(
    containerId: string, arrivalWeight: number, groupagePhotoUrl: string,
  ): Promise<{
    weightDelta: number; alertTriggered: boolean; alreadyWeighed: boolean; pending: string[];
    arrivalWeight: number; groupagePhotoUrl: string;
  }> {
    return this.containerService.weighContainerArrival(containerId, arrivalWeight, groupagePhotoUrl);
  }

  /** #2010 — colis du conteneur encore en route vers le Congo (lus au serveur). */
  pendingArrivalShipments(shipmentIds: string[]): Promise<string[]> {
    return this.containerService.pendingArrivalShipments(shipmentIds);
  }

  /** #2010 — fait passer en tri Congo les SEULS colis encore en route ; rend ceux qui y restent. */
  completeArrivalTransitions(container: ShippingContainer): Promise<string[]> {
    return this.containerService.completeArrivalTransitions(container);
  }

  scanParcelInContainer(containerId: string, trackingCode: string, arrivalWeight: number): Promise<{ found: boolean; weightDelta: number; shipmentInfo?: ReceptionSearchResult }> {
    return this.containerService.scanParcelInContainer(containerId, trackingCode, arrivalWeight);
  }

  completeUnpacking(containerId: string): Promise<void> {
    return this.containerService.completeUnpacking(containerId);
  }

  validateScannedParcel(shipmentId: string, status: 'validated' | 'blocked' | 'pending', photoUrl?: string): Promise<void> {
    return this.containerService.validateScannedParcel(shipmentId, status, photoUrl);
  }

  confirmFilmage(containerId: string, confirmedBy: string): Promise<void> {
    return this.containerService.confirmFilmage(containerId, confirmedBy);
  }

  saveGroupagePhoto(containerId: string, photoUrl: string): Promise<void> {
    return this.containerService.saveGroupagePhoto(containerId, photoUrl);
  }

  saveContainerLabel(containerId: string, labelUrl: string): Promise<void> {
    return this.containerService.saveContainerLabel(containerId, labelUrl);
  }

  setShippingMethod(
    containerId: string,
    method: 'air' | 'maritime',
    subType?: ShippingMethodSubType,
    partnerId?: string,
    partnerName?: string
  ): Promise<void> {
    return this.containerService.setShippingMethod(containerId, method, subType, partnerId, partnerName);
  }

  setExpeditionContact(
    containerId: string,
    role: 'responsableGroupageA' | 'contactExpedition' | 'contactReception' | 'responsableGroupageB',
    memberId: string,
    memberName: string
  ): Promise<void> {
    return this.containerService.setExpeditionContact(containerId, role, memberId, memberName);
  }

  openContactStep(containerId: string, openedBy: string): Promise<void> {
    return this.containerService.openContactStep(containerId, openedBy);
  }

  closeContactStep(containerId: string, closedBy: string): Promise<void> {
    return this.containerService.closeContactStep(containerId, closedBy);
  }

  updateExpeditionStep(containerId: string, step: string): Promise<void> {
    return this.containerService.updateExpeditionStep(containerId, step);
  }

  dispatchContainerWithNotifications(containerId: string, estimatedArrival: Date): Promise<{ success: boolean; notificationResults?: any }> {
    return this.containerService.dispatchContainerWithNotifications(containerId, estimatedArrival);
  }

  // ══ Délégations — Livraison / retour / réexpédition (DeliveryService) ══

  getShipmentsForSorting(limitCount = 200): Promise<ShipmentListItem[]> {
    return this.deliveryService.getShipmentsForSorting(limitCount);
  }

  assignDeliveryPartner(shipmentId: string, partnerId: string, partnerName: string): Promise<void> {
    return this.deliveryService.assignDeliveryPartner(shipmentId, partnerId, partnerName);
  }

  declareShipmentLost(shipmentId: string, containerId?: string, reason?: string): Promise<void> {
    return this.deliveryService.declareShipmentLost(shipmentId, containerId, reason);
  }

  confirmDeliverySorting(shipmentId: string): Promise<void> {
    return this.deliveryService.confirmDeliverySorting(shipmentId);
  }

  /** #416 — « Prêt au retrait » au point relais / entrepôt RDC. */
  markReadyForPickup(shipmentId: string, pickupLocation?: string, notes?: string): Promise<void> {
    return this.deliveryService.markReadyForPickup(shipmentId, pickupLocation, notes);
  }

  /** #1376 — rend la page (liste + plafond atteint ou non), plus un simple tableau. */
  getShipmentsForDelivery(partnerId?: string, limitCount = DELIVERY_LIST_LIMIT): Promise<DeliveryListPage> {
    return this.deliveryService.getShipmentsForDelivery(partnerId, limitCount);
  }

  getShipmentForDelivery(shipmentId: string): Promise<any> {
    return this.deliveryService.getShipmentForDelivery(shipmentId);
  }

  confirmDelivery(
    shipmentId: string,
    trackingNumber: string,
    proof: { deliveryPhotoUrl: string; recipientIdPhotoUrl: string; signatureUrl: string; receivedBy: string },
    receiverInfo: { name: string; phone: string; email?: string }
  ): Promise<void> {
    return this.deliveryService.confirmDelivery(shipmentId, trackingNumber, proof, receiverInfo);
  }

  recordFailedDelivery(
    shipmentId: string,
    reason: string,
    notes?: string,
    rescheduledTo?: Date,
    senderInfo?: { email?: string; phone?: string; name?: string },
    receiverInfo?: { email?: string; phone?: string; name?: string },
    trackingNumber?: string,
    deliveryMethod?: string
  ): Promise<void> {
    return this.deliveryService.recordFailedDelivery(shipmentId, reason, notes, rescheduledTo, senderInfo, receiverInfo, trackingNumber, deliveryMethod);
  }

  rescheduleDelivery(shipmentId: string, newDate: Date, partnerId?: string, partnerName?: string, notes?: string): Promise<void> {
    return this.deliveryService.rescheduleDelivery(shipmentId, newDate, partnerId, partnerName, notes);
  }

  confirmRedeliveryDispatch(shipmentId: string): Promise<void> {
    return this.deliveryService.confirmRedeliveryDispatch(shipmentId);
  }

  markForReturn(shipmentId: string, reason: string): Promise<void> {
    return this.deliveryService.markForReturn(shipmentId, reason);
  }

  markReturnReceived(shipmentId: string): Promise<void> {
    return this.deliveryService.markReturnReceived(shipmentId);
  }

  createReturnFeePaymentLink(shipmentId: string, amount: number, reason?: string): Promise<{ success: boolean; paymentLinkUrl: string; notifications?: { emailSent: boolean; smsSent: boolean } }> {
    return this.deliveryService.createReturnFeePaymentLink(shipmentId, amount, reason);
  }

  completeReturn(shipmentId: string, notes?: string, force = false): Promise<void> {
    return this.deliveryService.completeReturn(shipmentId, notes, force);
  }

  createReshipment(
    shipmentId: string,
    receiver: {
      firstName: string;
      lastName: string;
      email?: string;
      phone?: { prefix: string; number: string; country?: string };
      address: { street?: string; city: string; country?: string };
    },
    reason?: string
  ): Promise<{ success: boolean; reshipmentId: string; trackingNumber: string }> {
    return this.deliveryService.createReshipment(shipmentId, receiver, reason);
  }

  // ══ Délégations — Partenaires / équipes (PartnerService) ══

  getAllPartners(): Promise<DeliveryPartner[]> {
    return this.partnerService.getAllPartners();
  }

  getPartnerById(partnerId: string): Promise<DeliveryPartner | null> {
    return this.partnerService.getPartnerById(partnerId);
  }

  createPartner(data: Partial<DeliveryPartner>): Promise<string> {
    return this.partnerService.createPartner(data);
  }

  updatePartner(partnerId: string, data: Partial<DeliveryPartner>): Promise<void> {
    return this.partnerService.updatePartner(partnerId, data);
  }

  deletePartner(partnerId: string): Promise<void> {
    return this.partnerService.deletePartner(partnerId);
  }

  getAllTeamMembers(): Promise<TeamMember[]> {
    return this.partnerService.getAllTeamMembers();
  }

  getAllTeamMembersLight(): Promise<TeamMember[]> {
    return this.partnerService.getAllTeamMembersLight();
  }

  createTeamMember(data: Partial<TeamMember>): Promise<string> {
    return this.partnerService.createTeamMember(data);
  }

  updateTeamMember(memberId: string, data: Partial<TeamMember>): Promise<void> {
    return this.partnerService.updateTeamMember(memberId, data);
  }

  deleteTeamMember(memberId: string, reason: string): Promise<void> {
    return this.partnerService.deleteTeamMember(memberId, reason);
  }

  setEmployeeStatus(memberId: string, status: 'active' | 'inactive', reason: string): Promise<void> {
    return this.partnerService.setEmployeeStatus(memberId, status, reason);
  }

  inviteEmployee(data: {
    name: string;
    email: string;
    role: string;
    contractType?: string;
    location?: string;
    department?: string;
    phone?: string;
    groupIds?: string[];
    hireDate?: string;
    contractStartDate?: string;
    contractEndDate?: string;
    managerId?: string;
  }): Promise<{ teamMemberId: string; emailSent: boolean; failedGroups: string[] }> {
    return this.partnerService.inviteEmployee(data);
  }

  activateEmployee(token: string, password: string): Promise<{ email: string }> {
    return this.partnerService.activateEmployee(token, password);
  }

  resendInvitation(teamMemberId: string): Promise<{ emailSent: boolean }> {
    return this.partnerService.resendInvitation(teamMemberId);
  }

  getAllShippingPartners(): Promise<ShippingPartner[]> {
    return this.partnerService.getAllShippingPartners();
  }

  getShippingPartnersBySubType(subType: ShippingMethodSubType): Promise<ShippingPartner[]> {
    return this.partnerService.getShippingPartnersBySubType(subType);
  }

  getShippingPartner(id: string): Promise<ShippingPartner | null> {
    return this.partnerService.getShippingPartner(id);
  }

  createShippingPartner(data: Partial<ShippingPartner>): Promise<string> {
    return this.partnerService.createShippingPartner(data);
  }

  updateShippingPartner(id: string, data: Partial<ShippingPartner>): Promise<void> {
    return this.partnerService.updateShippingPartner(id, data);
  }

  deleteShippingPartner(id: string): Promise<void> {
    return this.partnerService.deleteShippingPartner(id);
  }

  isShippingPartnerInUse(partnerId: string): Promise<boolean> {
    return this.partnerService.isShippingPartnerInUse(partnerId);
  }

  archiveAndCreateV2(partnerId: string): Promise<string> {
    return this.partnerService.archiveAndCreateV2(partnerId);
  }

  getAllLogisticsTeamMembers(): Promise<LogisticsTeamMember[]> {
    return this.partnerService.getAllLogisticsTeamMembers();
  }

  getLogisticsTeamByRole(role: LogisticsRole): Promise<LogisticsTeamMember[]> {
    return this.partnerService.getLogisticsTeamByRole(role);
  }

  getLogisticsTeamMember(id: string): Promise<LogisticsTeamMember | null> {
    return this.partnerService.getLogisticsTeamMember(id);
  }

  createLogisticsTeamMember(data: Partial<LogisticsTeamMember>): Promise<string> {
    return this.partnerService.createLogisticsTeamMember(data);
  }

  updateLogisticsTeamMember(id: string, data: Partial<LogisticsTeamMember>): Promise<void> {
    return this.partnerService.updateLogisticsTeamMember(id, data);
  }

  deleteLogisticsTeamMember(id: string): Promise<void> {
    return this.partnerService.deleteLogisticsTeamMember(id);
  }

  // ══ Délégations — Activité (BackofficeActivityService) ══

  addActivityEvent(
    shipmentId: string,
    status: string,
    label: string,
    category: ActivityCategory,
    source: string,
    details: Record<string, any> = {},
    automated = false
  ): Promise<void> {
    return this.activity.addActivityEvent(shipmentId, status, label, category, source, details, automated);
  }
}
